
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useReferralSystem } from "@/hooks/use-referral-system";
import { TrendingUp, User, Calendar, DollarSign } from "lucide-react";
import { format } from "date-fns";

export const ReferralConversions = () => {
  const { conversions, isLoadingConversions } = useReferralSystem();

  if (isLoadingConversions) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Loading Conversions...</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="animate-pulse space-y-4">
              <div className="h-20 bg-gray-200 rounded"></div>
              <div className="h-20 bg-gray-200 rounded"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!conversions || conversions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Referral Conversions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <TrendingUp className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">No conversions yet.</p>
            <p className="text-sm text-gray-500 mt-2">
              Share your referral link to start earning commissions when people sign up!
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'default';
      case 'paid': return 'secondary';
      case 'cancelled': return 'destructive';
      default: return 'outline';
    }
  };

  const getSubscriptionColor = (type: string) => {
    switch (type) {
      case 'monthly': return 'bg-blue-100 text-blue-800';
      case 'team': return 'bg-green-100 text-green-800';
      case 'enterprise': return 'bg-purple-100 text-purple-800';
      case 'lifetime': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Group conversions by status for summary
  const statusSummary = conversions.reduce((acc, conv) => {
    acc[conv.status] = (acc[conv.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const totalEarned = conversions
    .filter(conv => conv.status === 'confirmed' || conv.status === 'paid')
    .reduce((sum, conv) => sum + conv.commission_amount, 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">${totalEarned.toFixed(2)}</div>
              <div className="text-sm text-gray-600">Total Earned</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold">{statusSummary.confirmed || 0}</div>
              <div className="text-sm text-gray-600">Confirmed</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold">{statusSummary.pending || 0}</div>
              <div className="text-sm text-gray-600">Pending</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold">{conversions.length}</div>
              <div className="text-sm text-gray-600">Total</div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Recent Conversions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {conversions.map((conversion) => (
              <div key={conversion.id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-gray-400" />
                    <div>
                      <div className="font-semibold">User #{conversion.referred_user_id.substring(0, 8)}</div>
                      <div className="text-sm text-gray-600">
                        {format(new Date(conversion.converted_at), 'MMM dd, yyyy HH:mm')}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getSubscriptionColor(conversion.subscription_type)}`}>
                        {conversion.subscription_type === 'monthly' ? 'Monthly' :
                         conversion.subscription_type === 'team' ? 'Team' :
                         conversion.subscription_type === 'enterprise' ? 'Enterprise' :
                         conversion.subscription_type.charAt(0).toUpperCase() + conversion.subscription_type.slice(1)}
                      </span>
                      <Badge variant={getStatusColor(conversion.status)}>
                        {conversion.status.charAt(0).toUpperCase() + conversion.status.slice(1)}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 text-green-600 font-semibold">
                      <DollarSign className="h-4 w-4" />
                      {conversion.commission_amount.toFixed(2)}
                    </div>
                  </div>
                </div>
                
                {conversion.confirmed_at && (
                  <div className="text-sm text-gray-600 flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Confirmed: {format(new Date(conversion.confirmed_at), 'MMM dd, yyyy')}
                  </div>
                )}
                
                {conversion.payment_processed_at && (
                  <div className="text-sm text-green-600 flex items-center gap-1 mt-1">
                    <DollarSign className="h-4 w-4" />
                    Paid: {format(new Date(conversion.payment_processed_at), 'MMM dd, yyyy')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
